import React, { useState, useEffect, useCallback } from 'react';

const initialTutorialSteps = [
  {
    target: '.diagram-dropzone',
    content: 'Start by uploading a PDF',
    position: 'bottom'
  },
  {
    target: '.menu-item.pdf-customization-menu',
    content: 'Use PDF Customization to fine-tune scaling, rotation, and placement',
    position: 'bottom'
  },
  {
    target: '.menu-item.page-layout-menu',
    content: 'Customize how many pages are grouped together in each signature',
    position: 'bottom'
  },
  {
    target: '.menu-item.markup-menu',
    content: 'Add page numbers to your booklet to make navigation easy',
    position: 'bottom'
  },
  {
    target: '.menu-item.bookbinding-menu',
    content: 'Enable guides to help with folding and binding your booklet',
    position: 'bottom'
  },
  {
    target: '.menu-item.debug-menu',
    content: 'Enable page numbers to preview the layout of your booklet',
    position: 'bottom'
  }
];

const postUploadSteps = [
  {
    target: '.diagram-section:first-child',
    content: 'This shows your input PDF pages in order. Any padding pages will be shown as blank.',
    position: 'bottom'
  },
  {
    target: '.diagram-section:last-child',
    content: 'This shows how your pages will be arranged in the final booklet. Each row represents a signature that will be folded together.',
    position: 'bottom'
  }
];

const exportTutorialSteps = [
  {
    target: '.export-setting-group:first-child',
    content: 'Choose the paper size that matches the paper you\'ll be printing on',
    position: 'bottom'
  },
  {
    target: '.export-setting-group:last-child',
    content: 'Select whether your printer supports automatic double-sided printing (duplex) or if you\'ll need to print the odd pages first, transfer the paper back to the print tray, then print the even pages.',
    position: 'bottom'
  }
];

const Tutorial = ({ onComplete, hasPdfUploaded, onTargetFocus, isExportOpen }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const [tutorialMode, setTutorialMode] = useState('initial');
  const [isEntering, setIsEntering] = useState(true);

  const currentTutorialSteps = tutorialMode === 'initial'
    ? initialTutorialSteps
    : tutorialMode === 'export'
      ? exportTutorialSteps
      : postUploadSteps;

  const completeTutorial = useCallback(() => {
    setIsVisible(false);
    if (tutorialMode === 'initial') {
      localStorage.setItem('tutorialComplete', 'true');
    } else if (tutorialMode === 'post-upload') {
      localStorage.setItem('postUploadTutorialComplete', 'true');
    } else if (tutorialMode === 'export') {
      localStorage.setItem('exportTutorialComplete', 'true');
    }
    if (onComplete) onComplete();
  }, [tutorialMode, onComplete]);

  useEffect(() => {
    if (tutorialMode === 'initial') {
      const timer = setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => {
          setIsEntering(false);
        }, 50);
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [tutorialMode]);

  useEffect(() => {
    if (hasPdfUploaded && !localStorage.getItem('postUploadTutorialComplete')) {
      setTutorialMode('post-upload');
      setCurrentStep(0);
      setIsEntering(true);
      setIsVisible(false);

      const timer = setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => {
          setIsEntering(false);
        }, 50);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [hasPdfUploaded]);

  useEffect(() => {
    if (isExportOpen && !localStorage.getItem('exportTutorialComplete')) {
      setTutorialMode('export');
      setCurrentStep(0);
      setIsEntering(true);
      setIsVisible(false);
      setTargetRect(null);

      const timer = setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => {
          setIsEntering(false);
        }, 50);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isExportOpen]);

  useEffect(() => {
    const handleKeyPress = () => {
      if (currentStep < currentTutorialSteps.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        completeTutorial();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentStep, currentTutorialSteps.length, completeTutorial]);

  useEffect(() => {
    if (isVisible) {
      const updateTargetRect = () => {
        const currentTarget = document.querySelector(currentTutorialSteps[currentStep].target);
        if (!currentTarget) {
          return;
        }

        const measureTarget = () => {
          const rect = currentTarget.getBoundingClientRect();
          setTargetRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            bottom: rect.bottom
          });
        };

        if (onTargetFocus) {
          const focusResult = onTargetFocus(currentTarget);
          if (focusResult && typeof focusResult.then === 'function') {
            focusResult.then(() => {
              window.requestAnimationFrame(measureTarget);
            });
          } else {
            window.requestAnimationFrame(measureTarget);
          }
        } else {
          measureTarget();
        }
      };

      updateTargetRect();
      
      let ticking = false;
      const scrollHandler = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            updateTargetRect();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
      window.addEventListener('resize', updateTargetRect);
      
      return () => {
        window.removeEventListener('scroll', scrollHandler);
        window.removeEventListener('resize', updateTargetRect);
      };
    }
  }, [currentStep, isVisible, currentTutorialSteps, onTargetFocus]);

  const changeStep = (newStep) => {
    setTimeout(() => {
      setCurrentStep(newStep);
    }, 300);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (currentStep < currentTutorialSteps.length - 1) {
        changeStep(currentStep + 1);
      } else {
        completeTutorial();
      }
    }
  };

  const handleProgressDotClick = (index) => {
    if (index < currentStep) {
      changeStep(index);
    }
  };

  if (!targetRect) return null;

  return (
    <div 
      className={`tutorial-overlay ${!isEntering ? 'visible' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className="tutorial-skip-container">
        <button 
          onClick={(e) => {
            e.stopPropagation();
            completeTutorial();
          }} 
          className="skip-tutorial"
        >
          Skip Tutorial
        </button>
      </div>
      <div className="tutorial-highlight"
        style={{
          position: 'fixed',
          top: targetRect.top - 12,
          left: targetRect.left - 12,
          width: targetRect.width + 24,
          height: targetRect.height + 24,
        }}
      />
      <div 
        className="tutorial-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: (() => {
            const position = currentTutorialSteps[currentStep].position;
            const padding = 24;
            
            switch (position) {
              case 'bottom':
                return targetRect.bottom + padding;
              case 'bottom-above':
                return targetRect.top - 160;
              case 'top':
                return targetRect.top - 80;
              default:
                return targetRect.bottom + padding;
            }
          })(),
          left: targetRect.left + (targetRect.width / 2),
          opacity: targetRect ? 1 : 0,
        }}
      >
        <p>{currentTutorialSteps[currentStep].content}</p>
        <div className="tutorial-controls">
          <div className="tutorial-progress">
            {currentTutorialSteps.map((_, index) => (
              <div 
                key={index} 
                className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'clickable' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleProgressDotClick(index);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tutorial; 